import Link from 'next/link';
import { Box, TableCell, TableRow, Typography } from '@mui/material';

import { LeaderboardTrader } from '@/types';
import { formatLeaderboardCurrency, getAvatarGradient } from '@/utils';
import { GradientAvatar } from './GradientAvatar';
import { TraderHoverCard } from './TraderHoverCard';

import { ROUTES } from '@/constants';

interface LeaderboardTableRowProps {
  entry: LeaderboardTrader;
  index: number;
  orderBy: 'PNL' | 'VOL';
  category?: string;
}

export const LeaderboardTableRow = ({
  entry,
  index,
  orderBy,
  category,
}: LeaderboardTableRowProps) => {
  const profitLoss = `${entry?.pnl > 0 ? '+' : ''}${formatLeaderboardCurrency(entry?.pnl || 0)}`;
  const profitLossValue = profitLoss == '$0' ? '-' : profitLoss;

  const volume = formatLeaderboardCurrency(entry?.volume || 0);
  const volumeValue = volume == '$0' ? '-' : volume;
  const pnlColor = entry?.pnl > 0 ? '#22c55e' : entry?.pnl < 0 ? '#ef4444' : 'text.primary';

  // Display either PNL or VOL based on the mobile sort dropdown setting
  const mobileDisplayValue = orderBy === 'VOL' ? volume : profitLoss;
  const mobileDisplayColor = orderBy === 'VOL' ? 'text.primary' : pnlColor;

  return (
    <TableRow
      sx={{
        '&:hover': { bgcolor: 'action.hover' },
        '&:last-child td': { borderBottom: 'none' },
      }}
    >
      <TableCell sx={{ py: 2, width: { xs: '40px', sm: '60px' }, px: { xs: 1, sm: 2 } }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
          {entry?.winRank || index + 1}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, displayPrint: 'none' }}>
          <GradientAvatar
            gradient={
              entry?.profileImage || getAvatarGradient(entry?.address || '', entry?.id, index)
            }
            badge=""
            badgeColor=""
          />
          <TraderHoverCard trader={entry} index={index} orderBy={orderBy}>
            <Typography
              component={Link}
              href={ROUTES.LEADERBOARD_PORTFOLIO(entry?.address || entry?.id || index.toString())}
              sx={{
                fontSize: '0.9375rem',
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: 110, sm: 200, md: 250 },
                display: 'inline-block',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main',
                },
              }}
            >
              {entry?.userName || 'Unknown Trader'}
            </Typography>
          </TraderHoverCard>
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ py: 2, pr: { xs: 2, sm: 4 } }}>
        <Typography
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: { xs: mobileDisplayColor, md: pnlColor },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
            {profitLossValue}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
            {mobileDisplayValue}
          </Box>
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={{ py: 2, display: { xs: 'none', md: 'table-cell' }, width: '120px' }}
      >
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 500, color: 'text.secondary' }}>
          {volumeValue}
        </Typography>
      </TableCell>
    </TableRow>
  );
};
